import type { LiveBall } from "./useLiveStats";
import { LEN, LINE, lengthIndex, lineIndex } from "./pitchData";
import { isLeftHander } from "./shotData";

const COLORS = ["#F4F5F8", "#FBD9B4", "#F6B26B", "#EE8B3A", "#D96A1C"];

type Cell = { b: number; r: number; w: number };

const grid = (balls: LiveBall[], left: boolean): Cell[][] => {
  const g: Cell[][] = Array.from({ length: 6 }, () => Array.from({ length: 5 }, () => ({ b: 0, r: 0, w: 0 })));
  balls.filter((x) => isLeftHander(x) === left).forEach((x) => {
    const li = lengthIndex(x.length), ci = lineIndex(x.line);
    if (li === null || ci === null) return;
    const cell = g[li][left ? 4 - ci : ci];
    cell.b++;
    cell.r += x.runs + (x.extraType === "WIDE" ? x.extraRuns : 0);
    if (x.wicketType && !/run.?out/i.test(x.wicketType)) cell.w++;
  });
  return g;
};

const Half = ({ balls, left, max }: { balls: LiveBall[]; left: boolean; max: number }) => {
  const g = grid(balls, left);
  const total = g.flat().reduce((a, c) => a + c.b, 0);
  const ax = left ? ["LEG", "", "", "", "OFF"] : ["OFF", "", "", "", "LEG"];
  return (
    <div className="pmg">
      <div className="ax">
        {ax.map((a, i) =>
          i === 2 ? (
            <span key={i} className="st">
              <svg width="12" height="14" viewBox="0 0 12 14" fill="none"><path d="M2 2v11M6 2v11M10 2v11M1 1.5h10" stroke="#687080" strokeWidth="1.4" /></svg>
            </span>
          ) : (
            <span key={i}>{a}</span>
          ),
        )}
      </div>
      <div className="hd">{left ? "Left-hand bat" : "Right-hand bat"}</div>
      <div className="cells">
        {g.map((row, li) =>
          row.map((c, ci) => {
            const k = c.b ? Math.min(4, 1 + Math.floor((c.b / max) * 3.99)) : 0;
            return (
              <i key={`${li}-${ci}`} className={c.w && k >= 3 ? "w" : ""} style={{ background: COLORS[k] }} title={`${LEN[li]}, ${LINE[left ? 4 - ci : ci]}: ${c.b} ball${c.b === 1 ? "" : "s"}, ${c.r} run${c.r === 1 ? "" : "s"}${c.w ? `, ${c.w} wicket${c.w > 1 ? "s" : ""}` : ""}`}>
                {c.w ? `${c.w}W` : ""}
              </i>
            );
          }),
        )}
      </div>
      {total ? null : <div className="none">No balls to {left ? "left" : "right"}-handers</div>}
    </div>
  );
};

/** Where one bowler pitched, split by the batter's hand. Only balls with both line and length recorded are placed. */
const PitchMap = ({ balls, recorded }: { balls: LiveBall[]; recorded: boolean }) => {
  const legal = balls.filter((x) => x.extraType !== "NO_BALL" && !/^NO_BALL/.test(x.extraType ?? ""));
  const placed = legal.filter((x) => lengthIndex(x.length) !== null && lineIndex(x.line) !== null);
  if (!recorded) {
    return <div className="pa-note">The scorer has not recorded line and length for this match.</div>;
  }
  if (!placed.length) {
    return <div className="pa-note">{legal.length ? "The scorer has not recorded line and length for this bowler's balls." : "No balls bowled yet."}</div>;
  }
  const max = Math.max(1, ...[false, true].flatMap((l) => grid(legal, l).flat().map((c) => c.b)));
  const labels = (
    <div className="pml">
      {LEN.map((l) => (
        <span key={l}>{l}</span>
      ))}
    </div>
  );
  return (
    <>
      <div className="pmw">
        <div className="pmb">
          {labels}
          <Half balls={legal} left={false} max={max} />
        </div>
        <div className="pmb">
          {labels}
          <Half balls={legal} left max={max} />
        </div>
      </div>
      <div className="pmkey">
        Fewer balls
        {COLORS.slice(1).map((c) => (
          <i key={c} style={{ background: c }} />
        ))}
        More balls
        <span style={{ marginLeft: "auto" }}>W = wicket</span>
      </div>
      {placed.length < legal.length ? <div className="pa-note" style={{ marginTop: 6 }}>{placed.length} of {legal.length} balls have line and length recorded.</div> : null}
    </>
  );
};

export default PitchMap;
